import { Integrations as IntegrationsBrowser } from "@sentry/browser";
import { Integrations } from "@sentry/tracing";
import { Severity } from "@sentry/types";
import * as Sentry from "@sentry/vue";
import { VueConstructor } from "vue";
import { Store } from "vuex";
import { MUTATION_TYPES } from "@/store/const";
import { StateRoot } from "@/store/state";
import { LoginData } from "@/lib/authHelper";
import config from "@/config";

interface ErrorDetails {
  tags?: Record<string, string>;
}
export class Logger {
  private log(e: Error | string, level: Severity, context: Record<string, any> = {}, data?: ErrorDetails) {
    if (!config.sentryKey) return;
    let strContext = JSON.stringify(context, null, 2);
    let attachment = "";
    if (strContext.length > 8000) {
      attachment = JSON.stringify(context);
      strContext = "";
    }
    Sentry.captureException(e, scope => {
      scope.setLevel(level);
      if (strContext.length > 0) scope.setContext("details", { data: strContext });
      scope.setTags({
        domain: document.location.hostname,
        ...data?.tags,
      });
      if (attachment.length > 0)
        scope.addEventProcessor(event => {
          const client = Sentry.getCurrentHub().getClient();

          const { host, path, projectId, port, protocol, user } = client!.getDsn()!;
          const endpoint = `${protocol}://${host}${port !== "" ? `:${port}` : ""}${
            path !== "" ? `/${path}` : ""
          }/api/${projectId}/events/${
            event.event_id
          }/attachments/?sentry_key=${user}&sentry_version=7&sentry_client=custom-javascript`;

          const formData = new FormData();
          const blob = new Blob([attachment], { type: "application/json" });
          formData.append("context", blob, `context_${event.event_id}.json`);
          fetch(endpoint, {
            method: "POST",
            body: formData,
          }).catch(ex => {
            // we have to catch this otherwise it throws an infinite loop in Sentry
            console.error(ex);
          });
          return event;
        });
      return scope;
    });
  }

  public debug(e: Error | string, context: Record<string, any> = {}, data?: ErrorDetails) {
    this.log(e, Severity.Debug, context, data);
  }

  public info(e: Error | string, context: Record<string, any> = {}, data?: ErrorDetails) {
    this.log(e, Severity.Info, context, data);
  }

  public warn(e: Error | string, context: Record<string, any> = {}, data?: ErrorDetails) {
    this.log(e, Severity.Warning, context, data);
  }

  public error(e: Error | string, context: Record<string, any> = {}, data?: ErrorDetails) {
    this.log(e, Severity.Error, context, data);
  }

  public addBreadcrumb(message: string, data: { [k: string]: any }) {
    Sentry.addBreadcrumb({
      message,
      category: "console",
      type: "Debug",
      data,
      level: Severity.Info,
      timestamp: new Date().getTime(),
    });
  }
}

export default new Logger();

export const setup = {
  install(Vue: VueConstructor, { store }: { store: Store<StateRoot> }) {
    if (!config.sentryKey) return;
    Sentry.init({
      Vue,
      dsn: config.sentryKey,
      integrations: [
        new Integrations.BrowserTracing(),
        new IntegrationsBrowser.GlobalHandlers({ onerror: true, onunhandledrejection: false }),
      ],
      ignoreErrors: ["ResizeObserver loop limit exceeded", "InvalidStateError: The object is in an invalid state"],
      // Set tracesSampleRate to 1.0 to capture 100%
      // of transactions for performance monitoring.
      // We recommend adjusting this value in production
      tracesSampleRate: 1.0,
      logErrors: true,
      environment: process.env.NODE_ENV ?? "development",
    });
    store.subscribe(mutation => {
      if (mutation.type === MUTATION_TYPES.SET_LOGIN_DATA) {
        const data = mutation.payload as LoginData;
        Sentry.setUser({
          id: data.user.id,
          username: data.user.username,
          email: data.user.email ?? undefined,
        });
      } else if (mutation.type === MUTATION_TYPES.LOGOUT) {
        Sentry.setUser(null);
      }
    });
  },
};
